import { z } from 'zod';
import { INSTALLER_SOURCES } from './game-profile.js';

/**
 * The plugin/mod installer (docs/DESIGN.md §7): search Modrinth (and CurseForge when an admin
 * has set a key), pick a compatible version, and hearthd downloads it into the profile's
 * folder. What is installed is read back from the folder itself plus .hearth/installed.json.
 */

export const installerSourceSchema = z.enum(INSTALLER_SOURCES);
export const installerKindSchema = z.enum(['plugin', 'mod']);
export type InstallerKind = z.infer<typeof installerKindSchema>;

/** A plain .jar file name in the installer folder (route params, manifest entries). */
export const installerFileNameSchema = z
  .string()
  .min(5)
  .max(255)
  .regex(/^[^/\\:]+\.jar$/i, 'A .jar file name.')
  .refine(
    (n) => !n.startsWith('.') && ![...n].some((c) => c.charCodeAt(0) < 0x20),
    'A .jar file name.',
  );

/** Provider ids: Modrinth's base62 ids and slugs, CurseForge's numbers. */
export const installerIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);

export const installerContextSchema = z.object({
  /** False when this server's game/egg has no installer (vanilla, or another game). */
  supported: z.boolean(),
  kind: installerKindSchema.nullable(),
  folder: z.string().nullable(),
  /**
   * The Minecraft version: the server's version variable when it is concrete, else what the
   * running server reports ("latest" → "26.3"); null when neither is known, in which case
   * versions are matched by loader only (and Hearth AI won't install).
   */
  gameVersion: z.string().nullable(),
  /** The variable's value when it isn't a concrete version, to explain the above. */
  gameVersionRaw: z.string().nullable(),
  /** Sources the caller can search, in order, with the loaders they filter by. */
  sources: z.array(z.object({ source: installerSourceSchema, loaders: z.array(z.string()) })),
  /** CurseForge: offered and configured, offered but no API key set, or not offered. */
  curseforge: z.enum(['enabled', 'not_configured', 'not_offered']),
});
export type InstallerContext = z.infer<typeof installerContextSchema>;

export const installerSearchQuerySchema = z.object({
  source: installerSourceSchema.default('modrinth'),
  query: z.string().trim().max(100).default(''),
  page: z.coerce.number().int().min(1).max(50).default(1),
});

export const installerProjectSummarySchema = z.object({
  source: installerSourceSchema,
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  author: z.string().nullable(),
  iconUrl: z.string().nullable(),
  downloads: z.number().int(),
  updatedAt: z.string().nullable(),
  /** The project's page on the provider's site. */
  url: z.string(),
});
export type InstallerProjectSummary = z.infer<typeof installerProjectSummarySchema>;

export const installerSearchResultSchema = z.object({
  hits: z.array(installerProjectSummarySchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type InstallerSearchResult = z.infer<typeof installerSearchResultSchema>;

export const installerDependencyTypeSchema = z.enum(['required', 'optional']);

export const installerVersionSchema = z.object({
  id: z.string(),
  name: z.string(),
  versionNumber: z.string(),
  type: z.enum(['release', 'beta', 'alpha']),
  publishedAt: z.string(),
  gameVersions: z.array(z.string()),
  loaders: z.array(z.string()),
  /** Built for one of this server's loaders and (when known) its game version. */
  compatible: z.boolean(),
  /** Why not, in a few words, when compatible is false. */
  incompatibleReason: z.string().nullable(),
  /** The jar hearthd would download, or null when the provider has none we can fetch. */
  file: z.object({ name: z.string(), size: z.number().int() }).nullable(),
  dependencies: z.array(
    z.object({
      projectId: z.string(),
      versionId: z.string().nullable(),
      type: installerDependencyTypeSchema,
    }),
  ),
});
export type InstallerVersion = z.infer<typeof installerVersionSchema>;

export const installerProjectSchema = installerProjectSummarySchema.extend({
  /** Newest first. */
  versions: z.array(installerVersionSchema),
});
export type InstallerProject = z.infer<typeof installerProjectSchema>;

export const installerProjectParamsSchema = z.object({
  server: z.string(),
  source: installerSourceSchema,
  project: installerIdSchema,
});

export const installerDependencySchema = z.object({
  project: installerProjectSummarySchema,
  type: installerDependencyTypeSchema,
  /** Already in the folder (managed by Hearth). */
  installed: z.boolean(),
  /** The version Hearth would install, or null when there is no compatible one. */
  version: z.object({ id: z.string(), versionNumber: z.string() }).nullable(),
});
export type InstallerDependency = z.infer<typeof installerDependencySchema>;

export const installerInstallBodySchema = z.object({
  source: installerSourceSchema,
  projectId: installerIdSchema,
  versionId: installerIdSchema,
  /** Dependencies the user agreed to install too; each gets its newest compatible version. */
  dependencies: z.array(installerIdSchema).max(20).default([]),
});

export const installerChangeResultSchema = z.object({
  installed: z.array(z.object({ file: z.string(), title: z.string(), versionNumber: z.string() })),
  /** Things not installed and why (a dependency with no compatible version, say). */
  skipped: z.array(z.object({ title: z.string(), reason: z.string() })),
  removed: z.array(z.string()),
  /** The game only loads plugins and mods when it starts. */
  restartRequired: z.boolean(),
});
export type InstallerChangeResult = z.infer<typeof installerChangeResultSchema>;

export const installedEntrySchema = z.object({
  file: z.string(),
  size: z.number().int(),
  modifiedAt: z.string(),
  /** What Hearth installed; null for jars put there by hand (no update button). */
  managed: z
    .object({
      source: installerSourceSchema,
      projectId: z.string(),
      versionId: z.string(),
      versionNumber: z.string(),
      title: z.string(),
      iconUrl: z.string().nullable(),
      installedAt: z.string(),
    })
    .nullable(),
  /** A newer compatible version, when one was found. */
  update: z.object({ versionId: z.string(), versionNumber: z.string() }).nullable(),
});
export type InstalledEntry = z.infer<typeof installedEntrySchema>;

export const installedListSchema = z.object({
  folder: z.string(),
  kind: installerKindSchema,
  /** False until the folder exists (nothing installed yet). */
  exists: z.boolean(),
  entries: z.array(installedEntrySchema),
  /** Whether looking for updates worked; 'failed' when the provider couldn't be reached. */
  updates: z.enum(['checked', 'failed']),
});
export type InstalledList = z.infer<typeof installedListSchema>;

export const installedFileParamsSchema = z.object({
  server: z.string(),
  file: installerFileNameSchema,
});

// --- Admin → Settings → Integrations ---------------------------------------------------

export const integrationSettingsSchema = z.object({
  curseforge: z.object({
    /** The key is write-only; this says whether one is stored. */
    apiKeySet: z.boolean(),
  }),
});
export type IntegrationSettings = z.infer<typeof integrationSettingsSchema>;

export const updateIntegrationSettingsSchema = z.object({
  curseforge: z.object({
    /** Omit to keep the stored key. */
    apiKey: z.string().trim().min(1).max(200).optional(),
    /** Remove the stored key (hides the CurseForge source). */
    clear: z.boolean().default(false),
  }),
});
